/**
 * Grafana Faro — full browser observability for the customer-facing Next.js app.
 *
 * Packages: faro-web-sdk · faro-web-tracing · faro-react · faro-instrumentation-replay
 */

import {
  initializeFaro,
  getWebInstrumentations,
  FetchTransport,
  LogLevel,
  type Faro,
} from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { ReplayInstrumentation } from "@grafana/faro-instrumentation-replay";
import { getPublicEnv, getPublicNodeEnv } from "../config/public-env";
import logger from "./logger";

// ── PII scrubbing (GDPR / NHS DSP Toolkit) ────────────────────────────────────
const PII_KEYS = new Set([
  "email",
  "nhsNumber",
  "dob",
  "nino",
  "phone",
  "address",
  "firstName",
  "lastName",
  "dateOfBirth",
  "password",
]);

const IGNORED_ERRORS: Array<string | RegExp> = [
  /ResizeObserver loop limit exceeded/,
  /Non-Error promise rejection captured/,
  /Loading chunk \d+ failed/,
  /AbortError/,
  // rrweb session-replay tries to clone MUI vendor-prefixed CSS into a sandboxed
  // CSSStyleSheet. Chrome rejects -moz-* / -ms-* rules with SyntaxError.
  // Replay still works — these are non-critical parse failures.
  /Error occurred during session replay/,
  /Failed to execute 'insertRule'/,
];

const IGNORED_URLS: Array<string | RegExp> = [
  /\/api\/auth\/login/,
  /\/api\/v1\/tenant\/auth/,
];

function scrubPII(obj: unknown): unknown {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return (obj as unknown[]).map(scrubPII);
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
      k,
      PII_KEYS.has(k) ? "[REDACTED]" : scrubPII(v),
    ])
  );
}

let _faro: Faro | null = null;
let currentTenantId = "";

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";

  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const value = part.trim();
    if (value.startsWith(prefix)) {
      return decodeURIComponent(value.slice(prefix.length));
    }
  }

  return "";
}

function getObservabilityTenant(): string {
  return (
    currentTenantId ||
    readCookie("tenant") ||
    getPublicEnv("NEXT_PUBLIC_OBSERVABILITY_TENANT") ||
    "anonymous"
  );
}

export function initialiseFaro(): Faro | null {
  if (typeof window === "undefined") return null;
  if (_faro) return _faro;

  const collectorUrl = getPublicEnv("NEXT_PUBLIC_FARO_URL");
  if (!collectorUrl) return null;

  const nodeEnv = getPublicNodeEnv();
  const backendUrl = getPublicEnv("NEXT_PUBLIC_BASE_URL");
  const appVersion =
    getPublicEnv("NEXT_PUBLIC_APP_VERSION") ||
    process.env.NEXT_PUBLIC_APP_VERSION ||
    "unknown";
  const observabilityTenantHeader =
    getPublicEnv("NEXT_PUBLIC_OBSERVABILITY_TENANT_HEADER") || "X-Scope-OrgID";
  const isDeployed = !["local", "development"].includes(nodeEnv);

  try {
    _faro = initializeFaro({
      app: {
        name: "customer-web",
        version: appVersion,
        environment: nodeEnv,
      },

      transports: [
        new FetchTransport({
          url: collectorUrl,
          bufferSize: 50,
          concurrency: 3,
          requestOptions: {
            headers: {
              [observabilityTenantHeader]: () => getObservabilityTenant(),
              "X-Wisterias-App": "customer-web",
            },
          },
          // keepalive:true causes Chrome to reject requests with a body > 64 KB
          // (the per-request keepalive quota). rrweb replay batches routinely
          // exceed this. Omit the flag — Faro buffers and retries on its own.
        }),
      ],

      ignoreErrors: IGNORED_ERRORS,
      ignoreUrls: IGNORED_URLS,

      // Scrub PII from every event before it leaves the browser.
      // Also drop rrweb CSS insertRule noise (log events — not caught by ignoreErrors).
      beforeSend: (event) => {
        if (event.payload && typeof event.payload === "object") {
          (event as any).payload = scrubPII(event.payload);
        }

        // rrweb emits console.error for every vendor-prefixed CSS rule Chrome rejects.
        // captureConsole:true forwards those as log events; drop them here.
        const p = event.payload as Record<string, unknown> | undefined;
        if (p) {
          const msg = String(p.message ?? p.value ?? "");
          if (
            msg.includes("insertRule") ||
            msg.includes("Error occurred during session replay")
          ) {
            return null;
          }
        }

        return event;
      },

      instrumentations: [
        ...getWebInstrumentations({
          // Forward console.warn / console.error to Faro
          captureConsole: true,
          // Track resource timing (fetch, XHR, scripts, images)
          enablePerformanceInstrumentation: true,
        }),

        // OTel distributed tracing — W3C traceparent links browser → backend spans
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: [
              new RegExp(
                backendUrl
                  ? backendUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                  : ".*"
              ),
            ],
          },
        }),

        // rrweb session replay — enable in deployed environments across
        // supported browsers. Faro masks inputs and text by default; the
        // beforeSend filter above also drops known replay CSS noise.
        ...(isDeployed ? [new ReplayInstrumentation()] : []),
      ],
    });
  } catch (e) {
    // Never let Faro initialisation crash the app — observability is non-critical
    logger.warn("[Faro] Failed to initialise — observability disabled", e);
    _faro = null;
    return null;
  }

  return _faro;
}

export function getFaro(): Faro | null {
  return _faro;
}

const toFaroLogLevel = (level: unknown): LogLevel => {
  if (typeof level === "number") {
    if (level >= 50) return LogLevel.ERROR;
    if (level >= 40) return LogLevel.WARN;
    if (level >= 30) return LogLevel.INFO;
    if (level >= 20) return LogLevel.DEBUG;
    return LogLevel.TRACE;
  }

  switch (level) {
    case "trace":
      return LogLevel.TRACE;
    case "debug":
      return LogLevel.DEBUG;
    case "warn":
      return LogLevel.WARN;
    case "error":
    case "fatal":
      return LogLevel.ERROR;
    default:
      return LogLevel.INFO;
  }
};

/**
 * Mirrors structured application logs into Faro after it has been initialised.
 * Context is reduced to scrubbed primitive fields because Faro's log contract
 * intentionally only accepts string context values.
 */
export function pushFaroLog(
  level: unknown,
  message: string,
  context: Record<string, unknown>
) {
  const scrubbed = scrubPII(context) as Record<string, unknown>;
  const safeContext = Object.fromEntries(
    Object.entries(scrubbed).flatMap(([key, value]) => {
      if (
        value === null ||
        ["string", "number", "boolean"].includes(typeof value)
      ) {
        return [[key, String(value)]];
      }
      return [];
    })
  );

  _faro?.api.pushLog([message], {
    context: safeContext,
    level: toFaroLogLevel(level),
  });
}

// ── User context ──────────────────────────────────────────────────────────────
export function setFaroUser(
  userId: string,
  tenantId: string,
  role?: string | number
) {
  currentTenantId = tenantId;
  _faro?.api.setUser({
    id: userId,
    attributes: { tenantId, role: String(role ?? "staff") },
  });
}

export function clearFaroUser() {
  currentTenantId = "";
  _faro?.api.setUser({});
}

// ── Custom business events ────────────────────────────────────────────────────
export const faroEvents = {
  carePlanViewed: (serviceUserId: string, section: string) =>
    _faro?.api.pushEvent("care_plan.viewed", { serviceUserId, section }),
  riskAssessmentOpened: (serviceUserId: string, assessmentId = "new") =>
    _faro?.api.pushEvent("risk_assessment.opened", {
      serviceUserId,
      assessmentId,
    }),
  supportPlanReviewed: (serviceUserId: string, planId: string) =>
    _faro?.api.pushEvent("support_plan.reviewed", { serviceUserId, planId }),
  dailyNoteCreated: (serviceUserId: string, category: string) =>
    _faro?.api.pushEvent("daily_note.created", { serviceUserId, category }),
  visitAssigned: (visitId: string, session: string) =>
    _faro?.api.pushEvent("roster.visit.assigned", { visitId, session }),
  pageView: (pageName: string, durationMs?: number) => {
    _faro?.api.pushEvent("page.view", {
      pageName,
      ...(durationMs !== undefined ? { durationMs: String(durationMs) } : {}),
    });
    if (durationMs !== undefined)
      _faro?.api.pushMeasurement({
        type: "page_load",
        values: { duration: durationMs },
      });
  },
};
